import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider } from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';
import { useAppSuppliedRedirectTo } from '../hooks/usePostAuthRedirectTo';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';
import sleepioTheme from '@/themes/sleepioTheme';

// Entry point for external services that still redirect to the old
// /activate/sleepio-canopy/:lob/:org_slug/:intent/email/callback format.
// Waits 1500ms for Supabase to settle the session before calling getSession(),
// then routes to /coverage (Create) or /complete (Login) via usePostAuthNavigation.

const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  // This screen is where the emailed link lands, which means a fresh browser tab with
  // nothing saved in it — so the address has to be read off this screen's own URL. It
  // used to look only in saved storage, which in a fresh tab is always empty, so it could
  // not honour an address even once one was being sent (PAT-1286).
  //
  // Absent stays absent, same reason as ActivationComplete.tsx.
  const navigatePostAuth = usePostAuthNavigation(useAppSuppliedRedirectTo());
  const posthog = usePostHog();

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[EmailCallback]', 'No session after magic link');
        posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
      }

      navigatePostAuth({ replace: true });
    };

    handleCallback().catch((err) => {
      logError('[EmailCallback]', 'Callback failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
